import { File as FileIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React, { FC, useState } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'

import { feedbackPreviewKind } from '@/lib/feedback-attachments'

export interface FeedbackAttachmentPreview {
  file: File
  // Object URL owned (and revoked) by the uploader; absent for types with no in-browser renderer
  url?: string
}

interface FeedbackAttachmentPreviewDialogProps {
  preview: FeedbackAttachmentPreview | null
  onClose: () => void
}

/**
 * Shows a selected attachment before it is submitted. The file only ever reaches this point after
 * passing the allowlist and byte-signature screening, so the blob rendered here is a known image,
 * video or PDF (never HTML or SVG that could run script).
 */
export const FeedbackAttachmentPreviewDialog: FC<FeedbackAttachmentPreviewDialogProps> = ({ preview, onClose }) => {
  const t = useTranslations('Feedback')
  // Tracked per file, so opening another attachment starts from a clean state
  const [failedFile, setFailedFile] = useState<File | null>(null)

  const renderBody = () => {
    const { file, url } = preview ?? {}
    const kind = file ? feedbackPreviewKind(file) : 'none'
    const markFailed = () => setFailedFile(file ?? null)

    if (!file || !url || kind === 'none' || failedFile === file) {
      return (
        <div className="flex flex-col items-center gap-2 p-10 text-center">
          <FileIcon className="size-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t('previewUnavailable')}</p>
        </div>
      )
    }

    if (kind === 'image') {
      return (
        // eslint-disable-next-line @next/next/no-img-element -- next/image cannot take a blob: URL
        <img src={url} alt={file.name} className="max-h-[70dvh] max-w-full object-contain" onError={markFailed} />
      )
    }

    if (kind === 'video') {
      // User's own screen recording; there is no caption track to offer
      // eslint-disable-next-line jsx-a11y/media-has-caption
      return <video src={url} controls className="max-h-[70dvh] max-w-full" onError={markFailed} />
    }

    return <iframe src={url} title={file.name} className="h-[70dvh] w-full border-0" />
  }

  return (
    <Dialog open={!!preview} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90dvh] w-[calc(100vw-2rem)] max-w-3xl flex-col gap-4">
        <DialogHeader className="pr-6">
          <DialogTitle className="truncate text-left text-base">{preview?.file.name}</DialogTitle>
          <DialogDescription className="sr-only">{t('previewTitle')}</DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto rounded-md bg-muted">
          {renderBody()}
        </div>
      </DialogContent>
    </Dialog>
  )
}

FeedbackAttachmentPreviewDialog.displayName = 'FeedbackAttachmentPreviewDialog'
